import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
  Animated,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import { Svg, Path, Circle } from 'react-native-svg';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import SharedMegaphoneIcon from '@/components/icons/MegaphoneIcon';
import Accordion from '@/components/Accordion';
import MeetingListCard from '@/components/MeetingListCard';
import FeaturedMeetingCard from '@/components/FeaturedMeetingCard';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import OnboardingLayout from '@/components/OnboardingLayout';
import onboardingService from '@/services/onboardingService';
import meetingPreferencesService from '@/services/meetingPreferencesService';
import { MeetingsService } from '@/services/meetingsService';
import {
  MeetingExperience,
  MeetingIntent,
  MeetingNeed,
  MeetingProfile,
  Meeting,
} from '@/types/meetings';

const STEPS = {
  WELCOME: 0,
  Q1: 1,
  Q2: 2,
  Q3: 3,
  RESULT: 4,
} as const;

type Step = (typeof STEPS)[keyof typeof STEPS];

function RadioCard({
  label,
  selected,
  onPress,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      style={[styles.radioCard, selected && styles.radioCardSelected]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={styles.radioLabel}>{label}</Text>
      <View style={[styles.radioButton, selected && styles.selectedRadio]}>
        {selected && <View style={styles.radioInner} />}
      </View>
    </TouchableOpacity>
  );
}



function WelcomeAccordion({
  title,
  content,
  open,
  onToggle,
}: {
  title: string;
  content: string;
  open: boolean;
  onToggle: () => void;
}) {
  // Thin wrapper around the shared <Accordion /> primitive that preserves the
  // onboarding card visual styling (border, padding, Inter typography).
  return (
    <Accordion
      title={title}
      open={open}
      onToggle={onToggle}
      style={styles.accordionCard}
      headerStyle={styles.accordionHeader}
      titleStyle={styles.accordionTitle}
    >
      <Text style={styles.accordionContent}>{content}</Text>
    </Accordion>
  );
}

function MiniSpinner() {
  const spin = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 1200,
        useNativeDriver: true,
      })
    ).start();
  }, []);

  const rotate = spin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <Animated.View style={{ transform: [{ rotate }] }}>
      <Svg width={40} height={40} viewBox="0 0 40 40" fill="none">
        <Circle
          cx={20}
          cy={20}
          r={15}
          stroke="#DAD8CF"
          strokeWidth={2.5}
          fill="none"
        />
        <Circle
          cx={20}
          cy={20}
          r={15}
          stroke="#015480"
          strokeWidth={2.5}
          strokeDasharray="70 24"
          strokeLinecap="round"
          fill="none"
          transform="rotate(-90, 20, 20)"
        />
      </Svg>
    </Animated.View>
  );
}

export default function EncontrosOnboardingScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const { userProfile } = useAuth();

  const [step, setStep] = useState<Step>(STEPS.WELCOME);
  const [experience, setExperience] = useState<MeetingExperience | null>(null);
  const [intent, setIntent] = useState<MeetingIntent | null>(null);
  const [need, setNeed] = useState<MeetingNeed | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedProfile, setSavedProfile] = useState<MeetingProfile | null>(null);
  const [matchedMeetings, setMatchedMeetings] = useState<Meeting[]>([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [userCount, setUserCount] = useState<string>('1.423');

  const formatUserCount = (count: number): string => {
    return count.toLocaleString('pt-BR').replace(',', '.');
  };

  const toggleAccordion = (index: number) => {
    setOpenAccordion((prev) => (prev === index ? null : index));
  };

  const firstName = userProfile?.full_name?.split(' ')[0] ?? '';

  const insets = useSafeAreaInsets();

  const goBack = useCallback(() => {
    Haptics.selectionAsync();
    if (step === STEPS.WELCOME) {
      // From the first (welcome) step, back should return to the previous
      // screen the user came from. Fall back to the Bill home tab only if
      // there is no navigation history to pop to.
      if (router.canGoBack()) {
        router.back();
      } else {
        router.replace('/diary');
      }
    } else {
      setStep((s) => (s - 1) as Step);
    }
  }, [step, router]);

  const saveAndShowResult = useCallback(async () => {
    if (!experience || !intent || !need) return;
    setSaving(true);

    const profile = meetingPreferencesService.deriveProfile(experience, intent);
    setSavedProfile(profile);

    setLoadingMeetings(true);

    try {
      // Task #124 §6 — unify result screen with /api/meetings/suggestions so
      // the meetings shown here are byte-identical to "Para Você" on the hub.
      // Order matters: preferences must be persisted FIRST so the server
      // reads the fresh derived_profile / Q1 / Q3 when ranking.
      await meetingPreferencesService.savePreferences({
        meeting_experience: experience,
        meeting_intent: intent,
        meeting_need: need,
        derived_profile: profile,
      });

      const [suggested, statsRes] = await Promise.all([
        MeetingsService.getSuggestions(5),
        fetch('https://mobile.padrinho.app/api/stats/users').then(r => r.json()).catch(() => null),
      ]);

      if (statsRes?.success && typeof statsRes.count === 'number') {
        setUserCount(formatUserCount(statsRes.count));
      }

      await onboardingService.markComplete('meeting_onboarding');
      onboardingService.clearCache();

      const matched = (suggested || []).slice(0, 3);
      meetingPreferencesService.setSuggestedMeetingIds(matched.map((m) => m.id));
      setMatchedMeetings(matched);
    } catch (err) {
      console.error('[EncontrosOnboarding] Save error:', err);
    } finally {
      setSaving(false);
      setLoadingMeetings(false);
      setStep(STEPS.RESULT);
    }
  }, [experience, intent, need]);

  const handleNextFromQ3 = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    saveAndShowResult();
    setStep(STEPS.RESULT);
  }, [saveAndShowResult]);


  const handleFinish = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    meetingPreferencesService.clearCache();
    router.replace('/encontros-online');
  }, [router]);

  const q1Options: { label: string; value: MeetingExperience }[] = [
    { label: t('meetings.onboarding.q1.options.never'), value: 'never' },
    { label: t('meetings.onboarding.q1.options.once'), value: 'once' },
    { label: t('meetings.onboarding.q1.options.few_times'), value: 'few_times' },
    { label: t('meetings.onboarding.q1.options.long_periods'), value: 'long_periods' },
    { label: t('meetings.onboarding.q1.options.for_someone'), value: 'for_someone' },
  ];

  const q2Options: { label: string; value: MeetingIntent }[] = [
    { label: t('meetings.onboarding.q2.options.iniciante'), value: 'iniciante' },
    { label: t('meetings.onboarding.q2.options.novo'), value: 'novo' },
    { label: t('meetings.onboarding.q2.options.visitante'), value: 'visitante' },
    { label: t('meetings.onboarding.q2.options.recaido'), value: 'recaido' },
    { label: t('meetings.onboarding.q2.options.itinerante'), value: 'itinerante' },
    { label: t('meetings.onboarding.q2.options.codependente'), value: 'codependente' },
  ];

  const q3Options: { label: string; value: MeetingNeed }[] = [
    { label: t('meetings.onboarding.q3.options.courage'), value: 'courage' },
    { label: t('meetings.onboarding.q3.options.information'), value: 'information' },
    { label: t('meetings.onboarding.q3.options.emotional_support'), value: 'emotional_support' },
    { label: t('meetings.onboarding.q3.options.talk_to_others'), value: 'talk_to_others' },
    { label: t('meetings.onboarding.q3.options.recovery_after_relapse'), value: 'recovery_after_relapse' },
    { label: t('meetings.onboarding.q3.options.learn_to_help'), value: 'learn_to_help' },
  ];

  const closeButton = (
    <TouchableOpacity
      onPress={() => router.replace('/encontros-online')}
      hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
    >
      <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <Path d="M18 6L6 18M6 6l12 12" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    </TouchableOpacity>
  );

  if (step === STEPS.WELCOME) {
    return (
      <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
        <PageHeader
          title={t('meetings.title')}
          onBack={goBack}
          showBackButton
          backgroundColor="#F9F8F3"
        />
        <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          <View style={styles.welcomeHeroSection}>
            <Image
              source={require('@/assets/images/meetings.png')}
              style={styles.welcomeIllustration}
              resizeMode="contain"
            />
            <View style={styles.welcomeTextBlock}>
              <Text style={styles.welcomeTitle}>
                {t('meetings.onboarding.welcome.title', { name: firstName })}
              </Text>
              <Text style={styles.welcomeSubtitle}>
                {t('meetings.onboarding.welcome.subtitle')}
              </Text>
            </View>
          </View>
          <View style={styles.accordionSection}>
            <WelcomeAccordion
              title={t('meetings.onboarding.welcome.accordion1.title')}
              content={t('meetings.onboarding.welcome.accordion1.content')}
              open={openAccordion === 0}
              onToggle={() => toggleAccordion(0)}
            />
            <WelcomeAccordion
              title={t('meetings.onboarding.welcome.accordion2.title')}
              content={t('meetings.onboarding.welcome.accordion2.content')}
              open={openAccordion === 1}
              onToggle={() => toggleAccordion(1)}
            />
            <WelcomeAccordion
              title={t('meetings.onboarding.welcome.accordion3.title')}
              content={t('meetings.onboarding.welcome.accordion3.content')}
              open={openAccordion === 2}
              onToggle={() => toggleAccordion(2)}
            />
          </View>
        </ScrollView>
        <View style={styles.footerWrap}>
          <PrimaryButton
            title={t('meetings.onboarding.welcome.cta')}
            onPress={() => {
              Haptics.selectionAsync();
              setStep(STEPS.Q1);
            }}
          />
        </View>
      </SafeAreaView>
    );
  }

  if (step === STEPS.Q1) {
    const canContinue = experience !== null;
    return (
      <OnboardingLayout
        progressWidth={33}
        header={
          <PageHeader
            title={t('meetings.title')}
            onBack={goBack}
            showBackButton
            backgroundColor="#F9F8F3"
          />
        }
      >
        <ScrollView contentContainerStyle={styles.scrollContentQ} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>1</Text>
            </View>
            <View style={styles.textContainer}>
              <Text style={styles.question}>{t('meetings.onboarding.q1.question')}</Text>
            </View>
          </View>
          <View style={styles.optionsSection}>
            {q1Options.map((opt) => (
              <RadioCard
                key={opt.value}
                label={opt.label}
                selected={experience === opt.value}
                onPress={() => {
                  Haptics.selectionAsync();
                  setExperience(opt.value);
                }}
              />
            ))}
          </View>
        </ScrollView>
        <View style={[styles.footerWrap, { paddingBottom: insets.bottom + 16 }]}>
          <PrimaryButton
            title={t('meetings.onboarding.next')}
            onPress={() => {
              Haptics.selectionAsync();
              setStep(STEPS.Q2);
            }}
            disabled={!canContinue}
          />
        </View>
      </OnboardingLayout>
    );
  }

  if (step === STEPS.Q2) {
    const canContinue = intent !== null;
    return (
      <OnboardingLayout
        progressWidth={66}
        header={
          <PageHeader
            title={t('meetings.title')}
            onBack={goBack}
            showBackButton
            backgroundColor="#F9F8F3"
          />
        }
      >
        <ScrollView contentContainerStyle={styles.scrollContentQ} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>2</Text>
            </View>
            <View style={styles.textContainer}>
              <Text style={styles.question}>{t('meetings.onboarding.q2.question')}</Text>
            </View>
          </View>
          <View style={styles.optionsSection}>
            {q2Options.map((opt) => (
              <RadioCard
                key={opt.value}
                label={opt.label}
                selected={intent === opt.value}
                onPress={() => {
                  Haptics.selectionAsync();
                  setIntent(opt.value);
                }}
              />
            ))}
          </View>
        </ScrollView>
        <View style={[styles.footerWrap, { paddingBottom: insets.bottom + 16 }]}>
          <PrimaryButton
            title={t('meetings.onboarding.next')}
            onPress={() => {
              Haptics.selectionAsync();
              setStep(STEPS.Q3);
            }}
            disabled={!canContinue}
          />
        </View>
      </OnboardingLayout>
    );
  }

  if (step === STEPS.Q3) {
    const canContinue = need !== null;
    return (
      <OnboardingLayout
        progressWidth={100}
        header={
          <PageHeader
            title={t('meetings.title')}
            onBack={goBack}
            showBackButton
            backgroundColor="#F9F8F3"
          />
        }
      >
        <ScrollView contentContainerStyle={styles.scrollContentQ} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>3</Text>
            </View>
            <View style={styles.textContainer}>
              <Text style={styles.question}>{t('meetings.onboarding.q3.question')}</Text>
            </View>
          </View>
          <View style={styles.optionsSection}>
            {q3Options.map((opt) => (
              <RadioCard
                key={opt.value}
                label={opt.label}
                selected={need === opt.value}
                onPress={() => {
                  Haptics.selectionAsync();
                  setNeed(opt.value);
                }}
              />
            ))}
          </View>
        </ScrollView>
        <View style={[styles.footerWrap, { paddingBottom: insets.bottom + 16 }]}>
          <PrimaryButton
            title={t('meetings.onboarding.next')}
            onPress={handleNextFromQ3}
            disabled={!canContinue}
            loading={saving}
          />
        </View>
      </OnboardingLayout>
    );
  }

  // Screen 5 — Result
  const matchCount = matchedMeetings.length;
  const resultTitle =
    matchCount === 1
      ? t('meetings.onboarding.result.titleSingular', { name: firstName })
      : t('meetings.onboarding.result.title', {
          name: firstName,
          count: String(matchCount),
        });

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <PageHeader
        title={t('meetings.title')}
        onBack={goBack}
        showBackButton={false}
        rightComponent={closeButton}
        backgroundColor="#F9F8F3"
      />
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {saving || loadingMeetings ? (
          <View style={styles.savingWrap}>
            <MiniSpinner />
            <Text style={styles.savingText}>
              {t('meetings.onboarding.result.saving')}
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.resultTopSection}>
              <View style={styles.resultIconWrap}>
                <SharedMegaphoneIcon size={20} isActive={true} />
              </View>
              <View style={styles.resultTextGroup}>
                <Text style={styles.resultTitle}>{resultTitle}</Text>
                <Text style={styles.resultSubtitle}>
                  {t('meetings.onboarding.result.subtitle', { userCount })}
                </Text>
              </View>
            </View>

            {matchedMeetings.length > 0 ? (
              <View style={styles.resultCardsSection}>
                <FeaturedMeetingCard
                  meeting={matchedMeetings[0]}
                  onPress={() => router.push(`/meeting/${matchedMeetings[0].id}`)}
                />
                {matchedMeetings.slice(1).map((m) => (
                  <MeetingListCard
                    key={m.id}
                    meeting={m}
                    onPress={() => router.push(`/meeting/${m.id}`)}
                  />
                ))}
              </View>
            ) : (
              <View style={styles.noMatchWrap}>
                <Text style={styles.noMatchTitle}>
                  {t('meetings.onboarding.result.fallback')}
                </Text>
                <Text style={styles.noMatchSubtitle}>
                  {t('meetings.onboarding.result.fallbackSubtitle')}
                </Text>
              </View>
            )}
          </>
        )}
      </ScrollView>
      <View style={styles.footerWrap}>
        <PrimaryButton
          title={t('meetings.onboarding.result.explore')}
          onPress={handleFinish}
          disabled={saving || loadingMeetings}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingBottom: 32,
  },
  scrollContentQ: {
    paddingBottom: 32,
  },
  footerWrap: {
    paddingHorizontal: 24,
    paddingBottom: 16,
    paddingTop: 12,
    backgroundColor: '#F9F8F3',
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  optionsSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
    flexDirection: 'column',
    width: '100%',
  },
  welcomeHeroSection: {
    alignItems: 'center',
    gap: 24,
    paddingTop: 8,
    paddingBottom: 8,
  },
  welcomeIllustration: {
    width: 240,
    height: 240,
  },
  welcomeTextBlock: {
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  welcomeTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Italic',
    color: '#151515',
    lineHeight: 28.8,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#56554D',
    lineHeight: 19.6,
    textAlign: 'center',
  },
  accordionSection: {
    gap: 8,
    paddingTop: 8,
    paddingBottom: 8,
  },
  accordionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    overflow: 'hidden',
    // Override the Accordion primitive's default inner padding so the
    // dropdown matches the more compact spacing used on the meeting detail
    // page (tipsCard). Padding is driven by accordionHeader / accordionContent.
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 24,
    gap: 16,
  },
  accordionTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    lineHeight: 19.6,
  },
  accordionContent: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 19.6,
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  question: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 31.2,
    width: 345,
  },
  radioCard: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    width: '100%',
    minHeight: 64,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  radioCardSelected: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  radioButton: {
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  selectedRadio: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#FFFFFF',
  },
  radioInner: {
    backgroundColor: '#015480',
    width: 12,
    height: 12,
    borderRadius: 6,
    flexShrink: 0,
  },
  radioLabel: {
    color: '#151515',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  resultTopSection: {
    alignItems: 'center',
    gap: 24,
    paddingTop: 24,
    paddingBottom: 24,
  },
  resultIconWrap: {
    width: 40,
    height: 40,
    backgroundColor: '#F0EEE4',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  resultTextGroup: {
    alignItems: 'center',
    gap: 8,
    width: 345,
  },
  resultTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Italic',
    color: '#151515',
    lineHeight: 31.2,
    textAlign: 'center',
  },
  resultSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 19.6,
    textAlign: 'center',
  },
  resultCardsSection: {
    gap: 24,
  },
  noMatchWrap: {
    backgroundColor: '#F0EEE4',
    borderRadius: 16,
    padding: 20,
    gap: 8,
    marginTop: 8,
  },
  noMatchTitle: {
    fontSize: 16,
    fontFamily: 'Inter_600SemiBold',
    color: '#151515',
    lineHeight: 22.4,
  },
  noMatchSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#7E7D75',
    lineHeight: 19.6,
  },
  savingWrap: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
    paddingVertical: 48,
  },
  savingText: {
    fontSize: 18,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 26,
    maxWidth: 220,
  },
});
